import React, { useState, useMemo, useRef } from "react";

import { Alert } from "services/Alert";
import styled, { css } from "styled-components";

import { ACCOUNT_PRICE_PRICE_UPLOAD_CSV_API, SMS_SUPPLIER_RATE_UPLOAD_CSV_METHOD } from "const/apiMethods";
import { SCREEN_MEDIA, USD_DEFAULT_CURRENCY_KEY, EUR_DEFAULT_CURRENCY_KEY } from "const";
import { servicePick, toStartDayUTCTime, toEndDayUTCTime } from "utils";
import { apiFormData } from "api/loginRoutes";
import { useWindowWidth } from "hooks";
import CustomUploaderSingle from "components/Form/CustomUploaderSingle";
import FormCheckbox from "components/Form/FormCheckbox";
import { CustomModal } from "components/base/BaseModal";
import CustomField from "components/Form/CustomField";
import { Spacer } from "components/base/Spacer";

import { DatePicker, InputNumber, Schema, SelectPicker, Tooltip } from "rsuite";
import Whisper from "components/Whisper";
import Form from "components/Form/CompatForm";

const fullPriceData = [
    { value: true, name: "Upload as full price" },
    { value: false, name: "Update existing and add new" },
];

const FILES_DEFAULT = [];
const { BooleanType, NumberType } = Schema.Types;


const formDefaultValue = {
    id_column: 1,
    name_column: 2,
    update_allocated_numbers_price: true,
};


export default (
    {
        sp_key = 1,
        cur_key,
        service,
        accountId,
        supplierId,
        show,
        isSupplier,
        accountServicePlanList,
        currency_list,
        paymentTermsList,
        supplierPriceList,
        pickedPrice,

        pickPrice,
        onSuccess,
        onClose,
        ...props
    }
) => {
    const defaultCurKey = servicePick(service, USD_DEFAULT_CURRENCY_KEY, EUR_DEFAULT_CURRENCY_KEY);

    const formRef = useRef();
    const [files, setFiles] = useState(FILES_DEFAULT);
    const [loading, setLoading] = useState(false);

    const windowWidth = useWindowWidth();

    const formCommonModel = Schema.Model({
        sp_key: NumberType()
            .isRequired("Required"),
        cur_key: NumberType()
            .isRequired("Required"),
        ...(isSupplier ? {}
            : {
                id_column: NumberType()
                    .isRequired("Required")
                    .min(1, "Min value 1")
                    .max(9, "Max value 9")
            }),
        name_column: NumberType()
            .min(1, "Min value 1")
            .max(9, "Max value 9"),
        ...(isSupplier ? {}
            : {
                rate_column: NumberType()
                    .isRequired("Required")
                    .min(1, "Min value 1")
                    .max(9, "Max value 9")
            }),
        full_price: BooleanType()
            .isRequired("Required"),
        ...(isSupplier ? {
            update_transit_prefixes: BooleanType()
            // .isRequired("Required")
        }
            : {
                update_allocated_numbers_price: BooleanType()
                // .isRequired("Required")
            }),
    });

    // submit
    const handleSubmit = () => {
        if (!formRef.current.check()) {
            return;
        }

        if (!files || !files.length) {
            Alert.error("Please upload CSV file with prices");
            return;
        }

        const formValue = formRef.current.getFormValue();

        const startDate = formValue.start_date
            ? toStartDayUTCTime(formValue.start_date).getTime() !== toStartDayUTCTime(new Date()).getTime()
                ? toStartDayUTCTime(formValue.start_date)
                : null
            : null;

        const endDate = formValue.end_date
            ? toEndDayUTCTime(formValue.end_date).getTime() !== toEndDayUTCTime(new Date()).getTime()
                ? toEndDayUTCTime(formValue.end_date)
                : null
            : null;

        const params = isSupplier
            ? {
                ...formValue,
                upload_file_list: formValue.files,
                start_date: startDate,
                target: {
                    "sms.supplier_id": supplierId,
                    "sms.supplier_price_id": pickedPrice?.id
                }
            }
            : {
                ...formValue,

                id_column: formValue.id_column
                    ? +formValue.id_column
                    : undefined,

                name_column: formValue.name_column
                    ? +formValue.name_column
                    : undefined,

                rate_column: formValue.rate_column
                    ? +formValue.rate_column
                    : undefined,

                start_date: startDate,
                end_date: endDate,

                target: {
                    account_id: accountId
                }
            };

        const dt = new DataTransfer();
        dt.items.add(files[0].blobFile);
        params.files = dt.files;
        delete params.file;

        const method = isSupplier ? SMS_SUPPLIER_RATE_UPLOAD_CSV_METHOD : ACCOUNT_PRICE_PRICE_UPLOAD_CSV_API;

        setLoading(true);
        apiFormData(method, params)
            .then(r => {
                if (!r) {
                    return;
                }

                if (r.validation_error) {
                    Alert.error(<>
                        <p><b>Error</b>: {r.validation_error}</p>
                        {r.line_number &&
                            <p>
                                {params.files && `${params.files[0].name}: `}
                                line <b>{r.line_number}</b><br />
                                {r.line_str}
                            </p>
                        }
                    </>, 10000);
                    return;
                }

                onSuccess && onSuccess(r);
                handleClose();

            })
            .finally(() => {
                setLoading(false);
            });
    };


    const handleClose = () => {
        setFiles([]);
        onClose && onClose();
    };

    const columnsText = isSupplier ? "MCC, MNC, Rate, Date (option)" : "ID, Destination, Rate";

    return (
        <CustomModal
            show
            showFooter
            title={"Upload Price"}
            loading={loading}
            onConfirm={handleSubmit}
            onClose={handleClose}
            {...props}
        >
            <StyledForm
                ref={formRef}
                model={formCommonModel}
                formDefaultValue={{
                    ...formDefaultValue,
                    sp_key,
                    cur_key: cur_key || defaultCurKey
                }}
            >
                {isSupplier ? <CustomField
                    accepter={SelectPicker}
                    data={supplierPriceList}
                    cleanable={false}
                    searchable={false}
                    valueKey="id"
                    labelKey="name"
                    placeholder="Select price"
                    label="Price"
                    value={pickedPrice?.id}
                    onChange={(id) => {
                        const priceValue = supplierPriceList.find(price => price?.id === id);

                        pickPrice(priceValue);
                    }}
                    style={{
                        width: 230
                    }}
                /> : <></>}

                <StyledField>
                    <Form.ControlLabel>Currency</Form.ControlLabel>
                    <Form.Control
                        disabled
                        errorPlacement={"topStart"}
                        data={currency_list}
                        accepter={SelectPicker}
                        name="cur_key"
                        labelKey="name"
                        valueKey="cur_key"
                        searchable={false}
                        cleanable={false}
                    />
                </StyledField>

                {!isSupplier && <StyledField>
                    <Form.ControlLabel>From date</Form.ControlLabel>
                    <Form.Control
                        errorPlacement="topStart"
                        accepter={DatePicker}
                        name="start_date"

                        ranges={[
                            { label: "Today", value: new Date() },
                            { label: "Tomorrow", value: (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d; })() }
                        ]}

                        disabledDate={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today;
                        }}

                        // mobile
                        oneTap={windowWidth < 480}
                        inline={windowWidth < 480}
                    />
                </StyledField>}

                {!isSupplier && <StyledField>
                    <Whisper
                        speaker={<Tooltip>Can be empty</Tooltip>}
                        trigger="hover"
                        placement="bottomStart"
                    >
                        <Form.ControlLabel>To date <span className="hint">*</span></Form.ControlLabel>
                    </Whisper>
                    <Form.Control
                        errorPlacement="topStart"
                        accepter={DatePicker}
                        name="end_date"

                        ranges={[
                            { label: "Today", value: new Date() },
                            { label: "Tomorrow", value: (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d; })() }
                        ]}
                        defaultValue={new Date(new Date().setHours(23, 59, 59, 59))}
                        disabledDate={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today;
                        }}

                        // mobile
                        oneTap={windowWidth < 480}
                        inline={windowWidth < 480}
                    />
                </StyledField>}

                <StyledField>
                    <Form.ControlLabel>File</Form.ControlLabel>
                    <Form.Control
                        errorPlacement={"topStart"}
                        name="file"
                        text="Upload file list"
                        files={files}
                        accepter={CustomUploaderSingle}
                        setFiles={setFiles}
                    />
                </StyledField>

                <Spacer size={15} />

                <StyledControlLabel>
                    * The CSV file must consist of 3 columns: {columnsText}. <br />
                </StyledControlLabel>

                {!isSupplier && <hr />}

                {!isSupplier && <FormRow>

                    <FormCol>
                        <StyledField>
                            <Form.ControlLabel>Column "ID" order</Form.ControlLabel>
                            <Form.Control
                                errorPlacement={"topStart"}
                                min={1}
                                max={9}
                                accepter={InputNumber}
                                name="id_column"
                            />
                        </StyledField>
                    </FormCol>

                    <FormCol>
                        <StyledField>
                            <Form.ControlLabel>Column "Subdestination" order</Form.ControlLabel>
                            <Form.Control
                                errorPlacement={"topStart"}
                                min={1}
                                max={9}
                                accepter={InputNumber}
                                name="name_column"
                            />
                        </StyledField>
                    </FormCol>

                    <FormCol>
                        <StyledField>
                            <Form.ControlLabel>Column "Rate" order</Form.ControlLabel>
                            <Form.Control
                                errorPlacement={"topStart"}
                                min={1}
                                max={9}
                                accepter={InputNumber}
                                name={`rate_column`}
                            />
                        </StyledField>
                    </FormCol>

                </FormRow>}

                <hr />

                <StyledField>
                    <Form.ControlLabel>Upload action</Form.ControlLabel>
                    <Form.Control
                        errorPlacement={"topStart"}
                        data={fullPriceData}
                        accepter={SelectPicker}
                        name="full_price"
                        labelKey="name"
                        valueKey="value"
                        searchable={false}
                        cleanable={false}
                    />
                </StyledField>

                <FormRow>
                    <FormCol>
                        {isSupplier
                            ? <StyledField isCheckbox>
                                <Form.ControlLabel>Update allocated prefixes</Form.ControlLabel>
                                <Form.Control
                                    errorPlacement={"topStart"}
                                    accepter={FormCheckbox}
                                    name="update_transit_prefixes"
                                />
                            </StyledField>
                            : <StyledField isCheckbox>
                                <Form.ControlLabel>Update allocated numbers</Form.ControlLabel>
                                <Form.Control
                                    errorPlacement={"topStart"}
                                    accepter={FormCheckbox}
                                    name="update_allocated_numbers_price"
                                />
                            </StyledField>}
                    </FormCol>
                </FormRow>

            </StyledForm>
        </CustomModal>
    );
};

const StyledForm = styled(Form)`
`;

const FormRow = styled.div`
    @media (min-width: ${SCREEN_MEDIA.sm.min}px) {
        display: flex;
        flex-wrap: wrap;
    }
`;

const StyledControlLabel = styled(Form.ControlLabel)`
    font-style: italic;
    opacity: 0.6;
`;

const FormCol = styled.div`
    @media (min-width: ${SCREEN_MEDIA.sm.min}px) {
        padding-left: 10px;
        padding-right: 10px;
        width: 50%;

        &:nth-child(2n - 1) {
            padding-left: 0
        }

        &:nth-child(2n) {
            padding-right: 0
        }
    }
`;

const StyledField = styled.div`
    padding-bottom: 10px;
    padding-top: 10px;

    @media (min-width: ${SCREEN_MEDIA.sm.min}px) {
        display: flex;
    }

    .rs-control-label {
        flex-shrink: 0;
        align-self: center;
        padding-right: 10px;
        width: 100%;

        .hint {
            color: var(--color-green);
        };

        @media (min-width: ${SCREEN_MEDIA.sm.min}px) {
            width: 150px;
        }
    }

    .rs-input-number {

        @media (min-width: ${SCREEN_MEDIA.sm.min}px) {
            max-width: 90px;
        }
    }

    ${props => props.isCheckbox && css`
        display: flex;
        
        @media (max-width: ${SCREEN_MEDIA.sm.max}px) {

            .rs-form-control-wrapper {
                width: auto;
            }

            .rs-control-label {
                width: auto;
                order: 1;
            }
        }
    `}
`;